/**
 * Ambient biome stems — continuous synthesized atmosphere per planet archetype.
 *
 * Each stem has two layers:
 * 1. Drone/texture bed — looping oscillators and noise
 * 2. Drift sequencer — sparse, irregular melodic fragments that emerge from
 *    and dissolve back into the texture (inspired by Astroneer/Machinefabriek)
 *
 * All stems run simultaneously; the AmbientManager crossfades by adjusting
 * per-stem GainNodes. Built from sfx_and_ambience_v3.html reference.
 */

import type { PlanetArchetype } from '../types';
import { getAudioContext, getAmbientBus } from './sfx';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AmbientStem {
  output: GainNode;
  cleanup(): void;
}

export type StemId = PlanetArchetype | 'space';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function loopingNoise(ctx: AudioContext, duration: number): AudioBufferSourceNode {
  const len = ctx.sampleRate * duration;
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  return src;
}

// ---------------------------------------------------------------------------
// Drift Sequencer — oblique melodic system
// Notes emerge from texture, dissolve back. Irregular timing, long tails,
// notes as color within the drone, not melody on top of it.
// ---------------------------------------------------------------------------

interface DriftOpts {
  type?: OscillatorType;
  noteLen?: number;
  gapMin?: number;
  gapMax?: number;
  vol?: number;
  attack?: number;
  release?: number;
  filterFreq?: number;
  filterQ?: number;
  skipChance?: number;
  driftCents?: number;
  harmonize?: boolean;
  harmInterval?: number;
  harmChance?: number;
}

interface DriftHandle {
  stop(): void;
}

export function driftSequencer(
  ctx: AudioContext,
  outputGain: GainNode,
  notes: number[],
  opts: DriftOpts = {},
): DriftHandle {
  const {
    type = 'sine',
    noteLen = 4.0,
    gapMin = 6.0,
    gapMax = 14.0,
    vol = 0.04,
    attack = 1.5,
    release = 2.5,
    filterFreq = 600,
    filterQ = 0.7,
    skipChance = 0.25,
    driftCents = 15,
    harmonize = false,
    harmInterval = 1.5,
    harmChance = 0.2,
  } = opts;

  let idx = 0;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let alive = true;

  function emitTone(
    dest: GainNode, freq: number, startTime: number, dur: number,
    p: { type: OscillatorType; vol: number; attack: number; release: number;
         filterFreq: number; filterQ: number; driftCents: number },
  ): void {
    const o = ctx.createOscillator();
    o.type = p.type;
    o.frequency.value = freq;
    o.detune.value = (Math.random() - 0.5) * p.driftCents;

    // Slow pitch drift during note
    const driftTarget = freq + (Math.random() - 0.5) * freq * 0.008;
    o.frequency.setValueAtTime(freq, startTime);
    o.frequency.linearRampToValueAtTime(driftTarget, startTime + dur);

    const eg = ctx.createGain();
    eg.gain.setValueAtTime(0, startTime);
    eg.gain.linearRampToValueAtTime(p.vol, startTime + Math.min(p.attack, dur * 0.4));
    const decayStart = startTime + dur - p.release;
    if (decayStart > startTime + p.attack) {
      eg.gain.setValueAtTime(p.vol, decayStart);
    }
    eg.gain.exponentialRampToValueAtTime(0.0001, startTime + dur);

    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = p.filterFreq + (Math.random() - 0.5) * p.filterFreq * 0.3;
    f.Q.value = p.filterQ;

    o.connect(f);
    f.connect(eg);
    eg.connect(dest);
    o.start(startTime);
    o.stop(startTime + dur + 0.2);
  }

  function playNote(): void {
    if (!alive) return;

    if (Math.random() < skipChance) {
      scheduleNext();
      return;
    }

    const freq = notes[idx % notes.length];
    idx++;
    if (freq === 0) { scheduleNext(); return; }

    const t = ctx.currentTime;
    const totalLen = noteLen + (Math.random() - 0.5) * 2;

    emitTone(outputGain, freq, t, totalLen, {
      type, vol, attack, release, filterFreq, filterQ, driftCents,
    });

    if (harmonize && Math.random() < harmChance) {
      const hFreq = freq * harmInterval;
      const hDelay = 0.5 + Math.random() * 2;
      emitTone(outputGain, hFreq, t + hDelay, totalLen - hDelay, {
        type, vol: vol * 0.6, attack: attack * 1.3, release,
        filterFreq: filterFreq * 0.8, filterQ, driftCents: driftCents * 1.5,
      });
    }

    scheduleNext();
  }

  function scheduleNext(): void {
    if (!alive) return;
    const gap = gapMin + Math.random() * (gapMax - gapMin);
    timeoutId = setTimeout(playNote, gap * 1000);
  }

  // Don't start immediately — wait a random amount
  timeoutId = setTimeout(playNote, (gapMin * 0.5 + Math.random() * gapMax * 0.5) * 1000);

  return {
    stop() { alive = false; if (timeoutId) clearTimeout(timeoutId); },
  };
}

// ---------------------------------------------------------------------------
// Stem Builders
// ---------------------------------------------------------------------------

function buildSpace(ctx: AudioContext, out: GainNode): AmbientStem {
  // Sub drone — 3 detuned sines
  const o1 = ctx.createOscillator(); o1.type = 'sine'; o1.frequency.value = 55;
  const o2 = ctx.createOscillator(); o2.type = 'sine'; o2.frequency.value = 57;
  const o3 = ctx.createOscillator(); o3.type = 'sine'; o3.frequency.value = 82.5;
  const dg = ctx.createGain(); dg.gain.value = 0.06;
  const l = ctx.createOscillator(); l.type = 'sine'; l.frequency.value = 0.08;
  const lg = ctx.createGain(); lg.gain.value = 2;
  l.connect(lg); lg.connect(o1.frequency);
  [o1, o2, o3].forEach(o => { o.connect(dg); o.start(); });
  dg.connect(out); l.start();

  // Drift — open intervals, very sparse, like distant stars pinging
  const drift = driftSequencer(ctx, out,
    [130.8, 196, 261.6, 196, 329.6, 261.6, 196, 130.8],
    { noteLen: 5, gapMin: 10, gapMax: 20, vol: 0.03, attack: 2.0, release: 2.5,
      filterFreq: 500, harmonize: true, harmInterval: 1.498, harmChance: 0.3 },
  );

  return {
    output: out,
    cleanup() {
      o1.stop(); o2.stop(); o3.stop(); l.stop(); drift.stop();
      o1.disconnect(); o2.disconnect(); o3.disconnect();
      dg.disconnect(); l.disconnect(); lg.disconnect();
    },
  };
}

function buildThalia(ctx: AudioContext, out: GainNode): AmbientStem {
  // Warm pads
  const pad1 = ctx.createOscillator(); pad1.type = 'sine'; pad1.frequency.value = 146.8;
  const pad2 = ctx.createOscillator(); pad2.type = 'sine'; pad2.frequency.value = 220;
  const pg = ctx.createGain(); pg.gain.value = 0.05;
  pad1.connect(pg); pad2.connect(pg); pg.connect(out);
  pad1.start(); pad2.start();

  // Soft sparkle — bandpass noise
  const noiseSrc = loopingNoise(ctx, 10);
  const nf = ctx.createBiquadFilter(); nf.type = 'bandpass'; nf.frequency.value = 3000; nf.Q.value = 3;
  const ng = ctx.createGain(); ng.gain.value = 0.012;
  noiseSrc.connect(nf); nf.connect(ng); ng.connect(out); noiseSrc.start();

  // Lydian fragments — raised 4th gives warmth without being sugary
  const drift = driftSequencer(ctx, out,
    [293.7, 370, 440, 415.3, 329.6, 293.7, 220, 370],
    { type: 'triangle', noteLen: 4.5, gapMin: 8, gapMax: 16, vol: 0.035,
      attack: 1.8, release: 2.0, filterFreq: 1200, harmonize: true,
      harmInterval: 1.335, harmChance: 0.25 },
  );

  return {
    output: out,
    cleanup() {
      pad1.stop(); pad2.stop(); noiseSrc.stop(); drift.stop();
      pad1.disconnect(); pad2.disconnect(); pg.disconnect();
      noiseSrc.disconnect(); nf.disconnect(); ng.disconnect();
    },
  };
}

function buildBruma(ctx: AudioContext, out: GainNode): AmbientStem {
  // Howling wind — bandpass noise with frequency + volume modulation
  const noiseSrc = loopingNoise(ctx, 10);
  const nf = ctx.createBiquadFilter(); nf.type = 'bandpass'; nf.frequency.value = 700; nf.Q.value = 2.5;
  const ng = ctx.createGain(); ng.gain.value = 0.06;
  noiseSrc.connect(nf); nf.connect(ng); ng.connect(out);

  const freqLfo = ctx.createOscillator(); freqLfo.type = 'sine'; freqLfo.frequency.value = 0.04;
  const flg = ctx.createGain(); flg.gain.value = 350;
  freqLfo.connect(flg); flg.connect(nf.frequency);

  const volLfo = ctx.createOscillator(); volLfo.type = 'sine'; volLfo.frequency.value = 0.07;
  const vlg = ctx.createGain(); vlg.gain.value = 0.04;
  volLfo.connect(vlg); vlg.connect(ng.gain);

  noiseSrc.start(); freqLfo.start(); volLfo.start();

  // Very sparse — notes almost lost in the wind
  const drift = driftSequencer(ctx, out,
    [220, 329.6, 261.6, 220, 196, 261.6],
    { noteLen: 5, gapMin: 12, gapMax: 25, vol: 0.025, attack: 2.5, release: 3.0,
      filterFreq: 400, skipChance: 0.4, driftCents: 25 },
  );

  return {
    output: out,
    cleanup() {
      noiseSrc.stop(); freqLfo.stop(); volLfo.stop(); drift.stop();
      noiseSrc.disconnect(); nf.disconnect(); ng.disconnect();
      freqLfo.disconnect(); flg.disconnect();
      volLfo.disconnect(); vlg.disconnect();
    },
  };
}

function buildTephra(ctx: AudioContext, out: GainNode): AmbientStem {
  // Sub drones with bass tremolo
  const o1 = ctx.createOscillator(); o1.type = 'sine'; o1.frequency.value = 35;
  const o2 = ctx.createOscillator(); o2.type = 'sine'; o2.frequency.value = 42;
  const droneGain = ctx.createGain(); droneGain.gain.value = 0.08;
  o1.connect(droneGain); o2.connect(droneGain); droneGain.connect(out);

  const bassLfo = ctx.createOscillator(); bassLfo.type = 'sine'; bassLfo.frequency.value = 0.06;
  const blg = ctx.createGain(); blg.gain.value = 0.06;
  bassLfo.connect(blg); blg.connect(droneGain.gain);

  // Lowpass noise with modulated cutoff
  const noiseSrc = loopingNoise(ctx, 10);
  const nf = ctx.createBiquadFilter(); nf.type = 'lowpass'; nf.frequency.value = 200; nf.Q.value = 1;
  const ng = ctx.createGain(); ng.gain.value = 0.06;
  noiseSrc.connect(nf); nf.connect(ng); ng.connect(out);

  const cutLfo = ctx.createOscillator(); cutLfo.type = 'sine'; cutLfo.frequency.value = 0.12;
  const clg = ctx.createGain(); clg.gain.value = 120;
  cutLfo.connect(clg); clg.connect(nf.frequency);

  o1.start(); o2.start(); bassLfo.start(); noiseSrc.start(); cutLfo.start();

  // Chromatic fragments — tritones and semitones, unsettling
  const drift = driftSequencer(ctx, out,
    [164.8, 233.1, 220, 174.6, 261.6, 233.1, 164.8, 155.6],
    { noteLen: 4, gapMin: 9, gapMax: 18, vol: 0.035, attack: 1.5, release: 2.5,
      filterFreq: 350, harmonize: true, harmInterval: 1.414, harmChance: 0.2,
      driftCents: 20 },
  );

  return {
    output: out,
    cleanup() {
      o1.stop(); o2.stop(); bassLfo.stop(); noiseSrc.stop(); cutLfo.stop(); drift.stop();
      o1.disconnect(); o2.disconnect(); droneGain.disconnect();
      bassLfo.disconnect(); blg.disconnect();
      noiseSrc.disconnect(); nf.disconnect(); ng.disconnect();
      cutLfo.disconnect(); clg.disconnect();
    },
  };
}

function buildMurex(ctx: AudioContext, out: GainNode): AmbientStem {
  // Harmonic hum — 4 sines
  const freqs = [220, 330, 440, 550];
  const oscs: OscillatorNode[] = [];
  const gains: GainNode[] = [];
  for (let i = 0; i < freqs.length; i++) {
    const o = ctx.createOscillator(); o.type = 'sine'; o.frequency.value = freqs[i];
    const ig = ctx.createGain(); ig.gain.value = 0.04 / (i + 1);
    o.connect(ig); ig.connect(out); o.start();
    oscs.push(o); gains.push(ig);
  }

  // High register, angular leaps — light scattering through crystal
  const drift1 = driftSequencer(ctx, out,
    [659.3, 830.6, 587.3, 932.3, 740, 1046.5, 587.3, 659.3],
    { noteLen: 3.5, gapMin: 7, gapMax: 15, vol: 0.025, attack: 1.0, release: 2.0,
      filterFreq: 2500, harmonize: true, harmInterval: 1.26, harmChance: 0.35,
      driftCents: 10 },
  );

  // Deep shadow — very rare, grounds the shimmer
  const drift2 = driftSequencer(ctx, out,
    [220, 261.6, 185, 220],
    { noteLen: 6, gapMin: 18, gapMax: 35, vol: 0.02, attack: 2.5, release: 3.0,
      filterFreq: 800, skipChance: 0.35, driftCents: 8 },
  );

  return {
    output: out,
    cleanup() {
      for (const o of oscs) { o.stop(); o.disconnect(); }
      for (const g of gains) g.disconnect();
      drift1.stop(); drift2.stop();
    },
  };
}

function buildPallor(ctx: AudioContext, out: GainNode): AmbientStem {
  // Near-silence: barely audible sub
  const sub = ctx.createOscillator(); sub.type = 'sine'; sub.frequency.value = 40;
  const og = ctx.createGain(); og.gain.value = 0.03;
  sub.connect(og); og.connect(out); sub.start();

  // Extremely sparse — half are skipped
  const drift = driftSequencer(ctx, out,
    [233.1, 174.6, 233.1, 155.6],
    { noteLen: 5, gapMin: 20, gapMax: 40, vol: 0.02, attack: 2.0, release: 3.0,
      filterFreq: 350, skipChance: 0.5, driftCents: 30,
      harmonize: true, harmInterval: 1.5, harmChance: 0.15 },
  );

  return {
    output: out,
    cleanup() {
      sub.stop(); drift.stop();
      sub.disconnect(); og.disconnect();
    },
  };
}

function buildPelago(ctx: AudioContext, out: GainNode): AmbientStem {
  // Submerged — sub drones + filtered noise
  const o1 = ctx.createOscillator(); o1.type = 'sine'; o1.frequency.value = 50;
  const o2 = ctx.createOscillator(); o2.type = 'sine'; o2.frequency.value = 75;
  const pg = ctx.createGain(); pg.gain.value = 0.06;
  o1.connect(pg); o2.connect(pg); pg.connect(out);
  o1.start(); o2.start();

  const noiseSrc = loopingNoise(ctx, 10);
  const nf = ctx.createBiquadFilter(); nf.type = 'lowpass'; nf.frequency.value = 300; nf.Q.value = 2;
  const ng = ctx.createGain(); ng.gain.value = 0.04;
  noiseSrc.connect(nf); nf.connect(ng); ng.connect(out); noiseSrc.start();

  // Pentatonic — notes drift and overlap like currents
  const drift1 = driftSequencer(ctx, out,
    [233.1, 277.2, 349.2, 311.1, 207.7, 233.1, 349.2, 277.2],
    { type: 'triangle', noteLen: 5, gapMin: 8, gapMax: 18, vol: 0.035,
      attack: 2.0, release: 2.5, filterFreq: 500,
      harmonize: true, harmInterval: 1.498, harmChance: 0.3, driftCents: 18 },
  );

  // Deep undercurrent — very slow bass motion
  const drift2 = driftSequencer(ctx, out,
    [116.5, 138.6, 155.6, 116.5],
    { noteLen: 7, gapMin: 15, gapMax: 30, vol: 0.025, attack: 3.0, release: 3.5,
      filterFreq: 300, skipChance: 0.3, driftCents: 12 },
  );

  return {
    output: out,
    cleanup() {
      o1.stop(); o2.stop(); noiseSrc.stop(); drift1.stop(); drift2.stop();
      o1.disconnect(); o2.disconnect(); pg.disconnect();
      noiseSrc.disconnect(); nf.disconnect(); ng.disconnect();
    },
  };
}

function buildOchrus(ctx: AudioContext, out: GainNode): AmbientStem {
  // Corroded — detuned sawtooth drones
  const o1 = ctx.createOscillator(); o1.type = 'sawtooth'; o1.frequency.value = 65;
  const o2 = ctx.createOscillator(); o2.type = 'sawtooth'; o2.frequency.value = 68;
  const dg = ctx.createGain(); dg.gain.value = 0.03;
  o1.connect(dg); o2.connect(dg); dg.connect(out);
  o1.start(); o2.start();

  // Metallic noise with volume modulation
  const noiseSrc = loopingNoise(ctx, 10);
  const nf = ctx.createBiquadFilter(); nf.type = 'bandpass'; nf.frequency.value = 1200; nf.Q.value = 4;
  const ng = ctx.createGain(); ng.gain.value = 0.015;
  noiseSrc.connect(nf); nf.connect(ng); ng.connect(out); noiseSrc.start();

  const nLfo = ctx.createOscillator(); nLfo.type = 'sine'; nLfo.frequency.value = 0.1;
  const nlg = ctx.createGain(); nlg.gain.value = 0.012;
  nLfo.connect(nlg); nlg.connect(ng.gain); nLfo.start();

  // Dissonant fragments — square wave, filtered dark, tritones
  const drift = driftSequencer(ctx, out,
    [185, 196, 261.6, 185, 233.1, 164.8, 174.6, 246.9],
    { type: 'square', noteLen: 3.5, gapMin: 10, gapMax: 22, vol: 0.018,
      attack: 1.2, release: 2.0, filterFreq: 600, filterQ: 1.5,
      harmonize: true, harmInterval: 1.414, harmChance: 0.25, driftCents: 22 },
  );

  return {
    output: out,
    cleanup() {
      o1.stop(); o2.stop(); noiseSrc.stop(); nLfo.stop(); drift.stop();
      o1.disconnect(); o2.disconnect(); dg.disconnect();
      noiseSrc.disconnect(); nf.disconnect(); ng.disconnect();
      nLfo.disconnect(); nlg.disconnect();
    },
  };
}

function buildViridis(ctx: AudioContext, out: GainNode): AmbientStem {
  // Overgrown — warm pads + high rustle
  const o1 = ctx.createOscillator(); o1.type = 'sine'; o1.frequency.value = 130.8;
  const o2 = ctx.createOscillator(); o2.type = 'sine'; o2.frequency.value = 196;
  const pg = ctx.createGain(); pg.gain.value = 0.04;
  o1.connect(pg); o2.connect(pg); pg.connect(out);
  o1.start(); o2.start();

  const noiseSrc = loopingNoise(ctx, 10);
  const nf = ctx.createBiquadFilter(); nf.type = 'bandpass'; nf.frequency.value = 5000; nf.Q.value = 2;
  const ng = ctx.createGain(); ng.gain.value = 0.008;
  noiseSrc.connect(nf); nf.connect(ng); ng.connect(out); noiseSrc.start();

  const rLfo = ctx.createOscillator(); rLfo.type = 'sine'; rLfo.frequency.value = 0.25;
  const rlg = ctx.createGain(); rlg.gain.value = 0.008;
  rLfo.connect(rlg); rlg.connect(ng.gain); rLfo.start();

  // Natural minor — notes tend downward, like things settling into growth
  const drift = driftSequencer(ctx, out,
    [329.6, 293.7, 261.6, 246.9, 220, 261.6, 329.6, 220],
    { type: 'triangle', noteLen: 4.5, gapMin: 9, gapMax: 18, vol: 0.03,
      attack: 1.8, release: 2.5, filterFreq: 900,
      harmonize: true, harmInterval: 1.2, harmChance: 0.3, driftCents: 15 },
  );

  return {
    output: out,
    cleanup() {
      o1.stop(); o2.stop(); noiseSrc.stop(); rLfo.stop(); drift.stop();
      o1.disconnect(); o2.disconnect(); pg.disconnect();
      noiseSrc.disconnect(); nf.disconnect(); ng.disconnect();
      rLfo.disconnect(); rlg.disconnect();
    },
  };
}

function buildSonora(ctx: AudioContext, out: GainNode): AmbientStem {
  // Resonant hollow — harmonic series with per-partial LFOs
  const freqs = [110, 220, 330, 440];
  const oscs: OscillatorNode[] = [];
  const lfos: OscillatorNode[] = [];
  const allGains: GainNode[] = [];

  for (let i = 0; i < freqs.length; i++) {
    const o = ctx.createOscillator(); o.type = 'sine'; o.frequency.value = freqs[i];
    const ig = ctx.createGain(); ig.gain.value = 0.12 / (i + 1);
    o.connect(ig); ig.connect(out); o.start();

    const l = ctx.createOscillator(); l.type = 'sine'; l.frequency.value = 0.05 + i * 0.03;
    const lg = ctx.createGain(); lg.gain.value = 0.04 / (i + 1);
    l.connect(lg); lg.connect(ig.gain); l.start();

    oscs.push(o); lfos.push(l); allGains.push(ig, lg);
  }

  // Harmonic series notes — fast attack, long ring, like striking the planet
  const drift = driftSequencer(ctx, out,
    [110, 164.8, 220, 277.2, 329.6, 220, 164.8, 110],
    { noteLen: 6, gapMin: 8, gapMax: 18, vol: 0.035, attack: 0.08, release: 4.0,
      filterFreq: 1800, harmonize: true, harmInterval: 2.0, harmChance: 0.35,
      driftCents: 5 },
  );

  return {
    output: out,
    cleanup() {
      for (const o of oscs) { o.stop(); o.disconnect(); }
      for (const l of lfos) { l.stop(); l.disconnect(); }
      for (const g of allGains) g.disconnect();
      drift.stop();
    },
  };
}

// ---------------------------------------------------------------------------
// Stem registry
// ---------------------------------------------------------------------------

const STEM_BUILDERS: Record<StemId, (ctx: AudioContext, out: GainNode) => AmbientStem> = {
  space: buildSpace,
  thalia: buildThalia,
  bruma: buildBruma,
  tephra: buildTephra,
  murex: buildMurex,
  pallor: buildPallor,
  pelago: buildPelago,
  ochrus: buildOchrus,
  viridis: buildViridis,
  sonora: buildSonora,
};

// ---------------------------------------------------------------------------
// Ambient Manager — crossfade between biome stems
// ---------------------------------------------------------------------------

const CROSSFADE_DURATION = 2.0; // seconds

export class AmbientManager {
  private stems: Map<StemId, AmbientStem> = new Map();
  private activeStem: StemId | null = null;
  private ctx: AudioContext;

  constructor() {
    const audioCtx = getAudioContext();
    const bus = getAmbientBus();
    if (!audioCtx || !bus) {
      throw new Error('Audio context not initialized — call initAudio() first');
    }
    this.ctx = audioCtx;

    // Build all stems, each with its own gain node → ambient bus
    for (const [id, builder] of Object.entries(STEM_BUILDERS)) {
      const stemGain = audioCtx.createGain();
      stemGain.gain.value = 0; // all start silent
      stemGain.connect(bus);
      const stem = builder(audioCtx, stemGain);
      this.stems.set(id as StemId, stem);
    }
  }

  /** Crossfade to the given biome stem. Pass 'space' for orbit/airborne. */
  setBiome(biome: StemId): void {
    if (biome === this.activeStem) return;

    const t = this.ctx.currentTime;

    // Fade out all stems
    for (const [id, stem] of this.stems) {
      if (id === biome) continue;
      stem.output.gain.cancelScheduledValues(t);
      stem.output.gain.setValueAtTime(stem.output.gain.value, t);
      stem.output.gain.linearRampToValueAtTime(0, t + CROSSFADE_DURATION);
    }

    // Fade in target stem
    const target = this.stems.get(biome);
    if (target) {
      target.output.gain.cancelScheduledValues(t);
      target.output.gain.setValueAtTime(target.output.gain.value, t);
      target.output.gain.linearRampToValueAtTime(1.0, t + CROSSFADE_DURATION);
    }

    // When on a planet surface, keep space at 0.15 in background
    if (biome !== 'space') {
      const spaceStem = this.stems.get('space');
      if (spaceStem) {
        spaceStem.output.gain.cancelScheduledValues(t);
        spaceStem.output.gain.setValueAtTime(spaceStem.output.gain.value, t);
        spaceStem.output.gain.linearRampToValueAtTime(0.15, t + CROSSFADE_DURATION);
      }
    }

    this.activeStem = biome;
  }

  /** Stop all stems and disconnect everything. */
  cleanup(): void {
    for (const stem of this.stems.values()) {
      stem.cleanup();
      stem.output.disconnect();
    }
    this.stems.clear();
    this.activeStem = null;
  }
}
